// AdminPage.tsx — register/delete repos, edit metric metadata.
// Requires increment.admin (the entry gate already kicks the user out
// if they're missing the scope; this page just exposes the controls).
import { useEffect, useState } from 'react';
import { unwrapJson, ui } from 'common';
import { apiClient } from '../api';
import type { Repo, MetricDef } from '../api';

export default function AdminPage() {
  const [repos, setRepos] = useState<Repo[]>([]);
  const [error, setError] = useState('');
  const [createForm, setCreateForm] = useState({ owner: '', name: '' });
  const [secretReveal, setSecretReveal] = useState<{ repo: string; secret: string } | null>(null);

  const loadRepos = async () => {
    try {
      const res = await apiClient.fetch('/api/v1/repos');
      const data = await unwrapJson<{ repos: Repo[] }>(res);
      setRepos(data.repos ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load repos');
    }
  };

  useEffect(() => {
    loadRepos();
  }, []);

  const createRepo = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const res = await apiClient.fetch('/api/v1/repos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ owner: createForm.owner.trim(), name: createForm.name.trim() }),
      });
      const repo = await unwrapJson<Repo>(res);
      setCreateForm({ owner: '', name: '' });
      if (repo.webhook_secret) {
        setSecretReveal({ repo: `${repo.owner}/${repo.name}`, secret: repo.webhook_secret });
      }
      await loadRepos();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create repo');
    }
  };

  const [confirm, confirmDialog] = ui.useConfirm();

  const deleteRepo = async (r: Repo) => {
    const ok = await confirm({
      title: 'Delete Repo',
      body: <>Delete <code>{r.owner}/{r.name}</code>? All commits and metric values go with it.</>,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      const res = await apiClient.fetch(`/api/v1/repos/${r.owner}/${r.name}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) await unwrapJson(res);
      await loadRepos();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const rotate = async (r: Repo) => {
    const ok = await confirm({
      title: 'Rotate Webhook Secret',
      body: <>Rotate the webhook secret for <code>{r.owner}/{r.name}</code>? The old secret stops working immediately.</>,
      confirmLabel: 'Rotate',
      variant: 'warning',
    });
    if (!ok) return;
    try {
      const res = await apiClient.fetch(`/api/v1/repos/${r.owner}/${r.name}/rotate-secret`, {
        method: 'POST',
      });
      const body = await unwrapJson<{ webhook_secret: string }>(res);
      setSecretReveal({ repo: `${r.owner}/${r.name}`, secret: body.webhook_secret });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rotate secret');
    }
  };

  return (
    <main>
      <h1>Admin</h1>
      <p style={{ opacity: 0.7 }}>
        Registering a repo returns a webhook secret you paste into gitea's webhook config
        (push events only, content type <code>application/json</code>, URL{' '}
        <code>https://increment.lab0.us/api/v1/webhooks/gitea</code>). The secret is shown
        once — rotate from this page if you lose it.
      </p>
      {error && <div className="alert alert-danger">{error}</div>}

      {secretReveal && (
        <div className="alert alert-warning" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <strong>{secretReveal.repo}</strong> webhook secret (save now — won't be shown again):
          <code style={{ wordBreak: 'break-all', background: 'rgba(0,0,0,0.2)', padding: '0.5rem' }}>
            {secretReveal.secret}
          </code>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setSecretReveal(null)}
            style={{ alignSelf: 'flex-start' }}
          >
            Dismiss
          </button>
        </div>
      )}

      <h2>Register repository</h2>
      <form onSubmit={createRepo} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        <input
          type="text"
          className="form-control"
          placeholder="owner (e.g. openfactory)"
          value={createForm.owner}
          required
          onChange={(e) => setCreateForm({ ...createForm, owner: e.target.value })}
          style={{ maxWidth: 240 }}
        />
        <input
          type="text"
          className="form-control"
          placeholder="name (e.g. openfactory)"
          value={createForm.name}
          required
          onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
          style={{ maxWidth: 240 }}
        />
        <button
          type="submit"
          className="btn btn-primary"
          disabled={!createForm.owner.trim() || !createForm.name.trim()}
        >
          Register
        </button>
      </form>

      <h2>Registered</h2>
      {repos.length === 0 ? (
        <div className="empty-state">No repos.</div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Repository</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {repos.map((r) => (
              <tr key={r.id}>
                <td>{r.owner}/{r.name}</td>
                <td style={{ opacity: 0.7 }}>{new Date(r.created_at).toLocaleString()}</td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => rotate(r)}
                    style={{ marginRight: '0.5rem' }}
                  >
                    Rotate secret
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => deleteRepo(r)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2>Metric metadata</h2>
      <p style={{ opacity: 0.7 }}>
        Pick a repo above to view its metrics, then edit each metric's unit, direction,
        and description below. New metric names appear automatically the first time they're
        submitted; everything is editable here.
      </p>
      <MetricsEditor repos={repos} />

      {confirmDialog}
    </main>
  );
}

function MetricsEditor({ repos }: { repos: Repo[] }) {
  const [selected, setSelected] = useState<string>('');
  const [metrics, setMetrics] = useState<MetricDef[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!selected) {
      setMetrics([]);
      return;
    }
    const [owner, name] = selected.split('/');
    (async () => {
      try {
        const res = await apiClient.fetch(`/api/v1/repos/${owner}/${name}/metrics`);
        const data = await unwrapJson<{ metrics: MetricDef[] }>(res);
        setMetrics(data.metrics ?? []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load metrics');
      }
    })();
  }, [selected]);

  const patch = async (m: MetricDef, body: Partial<Pick<MetricDef, 'unit' | 'direction' | 'description'>>) => {
    const [owner, name] = selected.split('/');
    try {
      const res = await apiClient.fetch(
        `/api/v1/repos/${owner}/${name}/metrics/${encodeURIComponent(m.name)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
      const updated = await unwrapJson<MetricDef>(res);
      setMetrics(metrics.map((x) => (x.id === updated.id ? updated : x)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    }
  };

  const [confirm, confirmDialog] = ui.useConfirm();

  const remove = async (m: MetricDef) => {
    const ok = await confirm({
      title: 'Delete Metric',
      body: <>Delete metric <code>{m.name}</code>? Its values across every commit go with it.</>,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    const [owner, name] = selected.split('/');
    try {
      const res = await apiClient.fetch(
        `/api/v1/repos/${owner}/${name}/metrics/${encodeURIComponent(m.name)}`,
        { method: 'DELETE' }
      );
      if (!res.ok && res.status !== 204) await unwrapJson(res);
      setMetrics(metrics.filter((x) => x.id !== m.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete metric');
    }
  };

  return (
    <div>
      <select
        className="form-select form-select-sm"
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        style={{ maxWidth: 280, marginBottom: '0.75rem' }}
      >
        <option value="">— pick a repo —</option>
        {repos.map((r) => (
          <option key={r.id} value={`${r.owner}/${r.name}`}>
            {r.owner}/{r.name}
          </option>
        ))}
      </select>
      {error && <div className="alert alert-danger">{error}</div>}
      {selected && metrics.length === 0 && (
        <div className="empty-state">No metrics yet for this repo.</div>
      )}
      {metrics.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Unit</th>
              <th>Direction</th>
              <th>Description</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {metrics.map((m) => (
              <tr key={m.id}>
                <td><code>{m.name}</code></td>
                <td>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    defaultValue={m.unit}
                    placeholder="ms, MB, %, …"
                    onBlur={(e) => {
                      if (e.target.value !== m.unit) patch(m, { unit: e.target.value });
                    }}
                  />
                </td>
                <td>
                  <select
                    className="form-select form-select-sm"
                    value={m.direction}
                    onChange={(e) => patch(m, { direction: e.target.value as MetricDef['direction'] })}
                  >
                    <option value="lower_is_better">lower is better</option>
                    <option value="higher_is_better">higher is better</option>
                  </select>
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    defaultValue={m.description}
                    placeholder="(optional)"
                    onBlur={(e) => {
                      if (e.target.value !== m.description) patch(m, { description: e.target.value });
                    }}
                  />
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => remove(m)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {confirmDialog}
    </div>
  );
}
